import React, { useState } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, SaveIcon, XIcon } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import { useData } from '../../contexts/DataContext';
import { useToast } from '../../contexts/ToastContext';
import { AllowanceTemplate } from '../../types/staff';
import EmptyState from '../../components/ui/EmptyState';
const AllowanceTemplates: React.FC = () => {
  const {
    allowanceTemplates,
    addAllowanceTemplate,
    updateAllowanceTemplate,
    deleteAllowanceTemplate
  } = useData();
  const {
    addToast
  } = useToast();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<AllowanceTemplate | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    defaultMinutes: 0,
    description: ''
  });
  // Handle opening add modal
  const handleAdd = () => {
    setFormData({
      name: '',
      defaultMinutes: 0,
      description: ''
    });
    setShowAddModal(true);
  };
  // Handle opening edit modal
  const handleEdit = (template: AllowanceTemplate) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      defaultMinutes: template.defaultMinutes,
      description: template.description || ''
    });
    setShowEditModal(true);
  };
  // Handle opening delete modal
  const handleDelete = (template: AllowanceTemplate) => {
    setSelectedTemplate(template);
    setShowDeleteModal(true);
  };
  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value
    }));
  };
  // Handle saving new template
  const handleSaveNew = async () => {
    try {
      if (!formData.name.trim()) {
        addToast('Template name is required', 'error');
        return;
      }
      await addAllowanceTemplate({
        name: formData.name,
        defaultMinutes: formData.defaultMinutes,
        description: formData.description
      });
      setShowAddModal(false);
      addToast('Allowance template added successfully', 'success');
    } catch (err) {
      if (err instanceof Error) {
        addToast(`Failed to add template: ${err.message}`, 'error');
      } else {
        addToast('Failed to add template', 'error');
      }
    }
  };
  // Handle updating template
  const handleUpdate = async () => {
    if (!selectedTemplate) return;
    try {
      if (!formData.name.trim()) {
        addToast('Template name is required', 'error');
        return;
      }
      await updateAllowanceTemplate(selectedTemplate.id, {
        name: formData.name,
        defaultMinutes: formData.defaultMinutes,
        description: formData.description
      });
      setShowEditModal(false);
      addToast('Allowance template updated successfully', 'success');
    } catch (err) {
      if (err instanceof Error) {
        addToast(`Failed to update template: ${err.message}`, 'error');
      } else {
        addToast('Failed to update template', 'error');
      }
    }
  };
  // Handle deleting template
  const handleConfirmDelete = async () => {
    if (!selectedTemplate) return;
    try {
      await deleteAllowanceTemplate(selectedTemplate.id);
      setShowDeleteModal(false);
      addToast('Allowance template deleted successfully', 'success');
    } catch (err) {
      if (err instanceof Error) {
        addToast(`Failed to delete template: ${err.message}`, 'error');
      } else {
        addToast('Failed to delete template', 'error');
      }
    }
  };
  return <div>
      <PageHeader title="Allowance Templates" description="Manage allowance types and their default minutes" actions={<Button variant="primary" leftIcon={<PlusIcon className="h-4 w-4" />} onClick={handleAdd}>
            Add Template
          </Button>} />
      <Card>
        <div className="p-4 border-b border-ink-200">
          <h3 className="text-card-title text-ink-900">
            Available Allowance Templates
          </h3>
          <p className="mt-1 text-sm text-ink-500">
            These templates will be available when adding allowances to staff members.
            <strong> Note:</strong> Allowance minutes are automatically applied pro-rata based on staff FTE 
            (e.g., a 0.5 FTE staff member receives 50% of the template minutes).
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-ink-200">
            <thead className="bg-ink-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                  Default Minutes
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-ink-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-ink-200">
              {allowanceTemplates.map(template => <tr key={template.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-ink-900">
                      {template.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-mono tabular-nums text-sm text-ink-900">
                      {template.defaultMinutes} minutes
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-ink-500 line-clamp-2">
                      {template.description || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button onClick={() => handleEdit(template)} className="text-navy-600 hover:text-navy-800">
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(template)} className="text-danger-fg hover:text-danger-fg">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>)}
              {allowanceTemplates.length === 0 && <tr>
                  <td colSpan={4} className="px-6 py-4">
                    <EmptyState
                      icon={<PlusIcon className="h-6 w-6" />}
                      title="No allowance templates found"
                      description="These templates appear when adding allowances to staff members."
                      action={<Button variant="primary" leftIcon={<PlusIcon className="h-4 w-4" />} onClick={handleAdd}>
                        Add Template
                      </Button>}
                    />
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>
      {/* Add Template Modal */}
      {showAddModal && <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={() => setShowAddModal(false)}>
              <div className="absolute inset-0 bg-ink-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-ink-900" id="modal-title">
                      Add Allowance Template
                    </h3>
                    <div className="mt-4 space-y-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-ink-700">
                          Template Name *
                        </label>
                        <input type="text" id="name" name="name" className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={formData.name} onChange={handleInputChange} required />
                      </div>
                      <div>
                        <label htmlFor="defaultMinutes" className="block text-sm font-medium text-ink-700">
                          Default Minutes
                        </label>
                        <input type="number" id="defaultMinutes" name="defaultMinutes" min="0" max="2220" step="5" className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={formData.defaultMinutes} onChange={handleInputChange} />
                      </div>
                      <div>
                        <label htmlFor="description" className="block text-sm font-medium text-ink-700">
                          Description
                        </label>
                        <textarea id="description" name="description" rows={3} className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={formData.description} onChange={handleInputChange} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-ink-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-navy-600 text-base font-medium text-white hover:bg-navy-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-500 sm:ml-3 sm:w-auto sm:text-sm" onClick={handleSaveNew}>
                  Add Template
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-ink-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-ink-700 hover:bg-ink-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Edit Template Modal */}
      {showEditModal && <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={() => setShowEditModal(false)}>
              <div className="absolute inset-0 bg-ink-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-ink-900" id="modal-title">
                      Edit Allowance Template
                    </h3>
                    <div className="mt-4 space-y-4">
                      <div>
                        <label htmlFor="edit-name" className="block text-sm font-medium text-ink-700">
                          Template Name *
                        </label>
                        <input type="text" id="edit-name" name="name" className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={formData.name} onChange={handleInputChange} required />
                      </div>
                      <div>
                        <label htmlFor="edit-defaultMinutes" className="block text-sm font-medium text-ink-700">
                          Default Minutes
                        </label>
                        <input type="number" id="edit-defaultMinutes" name="defaultMinutes" min="0" max="2220" step="5" className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={formData.defaultMinutes} onChange={handleInputChange} />
                      </div>
                      <div>
                        <label htmlFor="edit-description" className="block text-sm font-medium text-ink-700">
                          Description
                        </label>
                        <textarea id="edit-description" name="description" rows={3} className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={formData.description} onChange={handleInputChange} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-ink-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-navy-600 text-base font-medium text-white hover:bg-navy-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-500 sm:ml-3 sm:w-auto sm:text-sm" onClick={handleUpdate}>
                  Update Template
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-ink-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-ink-700 hover:bg-ink-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Delete Confirmation Modal */}
      {showDeleteModal && <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={() => setShowDeleteModal(false)}>
              <div className="absolute inset-0 bg-ink-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-danger-bg sm:mx-0 sm:h-10 sm:w-10">
                    <XIcon className="h-6 w-6 text-danger-fg" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-ink-900" id="modal-title">
                      Delete Allowance Template
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-ink-500">
                        Are you sure you want to delete the template "
                        {selectedTemplate?.name}"? This action cannot be undone.
                      </p>
                      <p className="mt-2 text-sm text-danger-fg">
                        Note: This will not remove the allowance from staff
                        members who already have it assigned, but it will no
                        longer be available for new assignments.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-ink-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-danger-solid text-base font-medium text-white hover:bg-danger-solid focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-danger-solid sm:ml-3 sm:w-auto sm:text-sm" onClick={handleConfirmDelete}>
                  Delete
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-ink-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-ink-700 hover:bg-ink-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};
export default AllowanceTemplates;